import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, RefreshCwIcon } from 'lucide-react';
import { ScreenHeader } from '../components/ScreenHeader';
import { MetaLabel } from '../components/MetaLabel';
import { useShoot } from '../contexts/ShootContext';
import type { ScheduleStatus } from '../types/shoot';
const EASE = [0.23, 1, 0.32, 1] as const;
function Marker({
  status
}: {
  status: ScheduleStatus;
}) {
  if (status === 'complete') {
    return <span className="flex h-6 w-6 items-center justify-center border border-line text-dim" data-id="element-117" data-mp="vsceq4e12yxe">
        <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-118" data-mp="h3yd1wsclgif" />
      </span>;
  }
  if (status === 'current') {
    return <span className="flex h-6 w-6 items-center justify-center bg-fg text-ink" data-id="element-119" data-mp="m0yhk39jub7f">
        <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={2.5} data-id="element-120" data-mp="19qsv6n1p03sq" />
      </span>;
  }
  return <span className="block h-6 w-6 rounded-full border border-lineHi" data-id="element-121" data-mp="1eiu1lsc6j0au" />;
}
export function Schedule() {
  const {
    schedule,
    statusFor,
    nowIndex,
    setCurrentTo,
    resumeAuto,
    scheduleMode,
    role,
    shootDayPhase,
    production
  } = useShoot();
  const current = schedule[nowIndex];
  const [openId, setOpenId] = useState<string | null>(current?.id ?? null);
  if (schedule.length === 0) {
    return <div className="pb-10" data-id="element-122" data-mp="folfoahvxehh">
        <ScreenHeader eyebrow="Schedule" title="Nothing yet" meta="No schedule has been added for this shoot." data-id="element-123" data-mp="fnad2e06ecry" />
      </div>;
  }

  // 9 Sep 2026: nowIndex sits outside the schedule (-1, or at/past the
  // last item) whenever today isn't the shoot's own calendar date — see
  // ShootContext.tsx's shootDayPhase — and now also intraday, on the
  // shoot's own day, once the clock passes the last item's end time (the
  // clock-driven auto-advance can reach the end of the list on its own,
  // not just via the day-level before/after gating). `current` is
  // undefined in both cases, so the header needs a fallback rather than
  // assuming there's always a "now" to point at.
  const isPastEnd = nowIndex >= schedule.length;
  const headerTitle = current ? current.title : isPastEnd ? 'Shoot complete' : 'Not started yet';
  const headerMeta = current ? `Running now · ${current.start}${current.end ? `–${current.end}` : ''}` : isPastEnd ? shootDayPhase === 'after' ? 'This shoot has already taken place.' : "All items on today's schedule have wrapped." : `Scheduled for ${production.date || 'a later date'}${schedule[0] ? ` · first item ${schedule[0].start}` : ''}`;

  // 9 Sep 2026: crew/staff can correct "current" directly — tap any item
  // below to mark it (see the "Mark As Current" button in each row's
  // detail) — including backward, for when a shoot is running behind and
  // the clock-driven auto-advance (see ShootContext.tsx) has moved past
  // where the crew actually is. Hidden for clients (their token isn't
  // accepted by advance_shoot_day_schedule) and outside the shoot's own
  // day, since there's nothing real to correct before or after it.
  const canEditSchedule = role !== 'client' && shootDayPhase !== 'before' && shootDayPhase !== 'after';
  return <div className="pb-10" data-id="element-124" data-mp="2fz85br70d9jx">
      <ScreenHeader eyebrow="Schedule" title={headerTitle} meta={headerMeta} data-id="element-125" data-mp="2ax8yu8fz1wir" />

      {/* Only shown once someone has actually corrected the position — in
          the normal auto-advancing case there's nothing to surface here,
          keeping the screen quiet. */}
      {canEditSchedule && scheduleMode === 'manual' ? <div className="flex items-center justify-between gap-4 border-b border-line bg-surface px-5 py-4" data-id="element-126" data-mp="2yoo68fmlv40v">
          <div className="min-w-0" data-id="element-127" data-mp="2tv23bf68uei9">
            <MetaLabel tone="dim" data-id="element-128" data-mp="2tua9o9w7iqum">Manually set</MetaLabel>
            <p className="mt-1 text-[13px] text-muted" data-id="element-129" data-mp="2ows7r3glxbw4">Not following the clock right now.</p>
          </div>
          <button type="button" onClick={resumeAuto} className="flex h-11 shrink-0 items-center gap-2 border border-line px-3 text-[10px] font-medium uppercase tracking-label text-muted transition-colors duration-150 ease-out hover:border-lineHi hover:text-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-fg" data-id="element-130" data-mp="3meih4rnd6iih">
            <RefreshCwIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-131" data-mp="2s6tcmhfd0q8w" />
            Resume Auto
          </button>
        </div> : null}

      <ol className="mt-2" data-id="element-132" data-mp="2x77d9eemsxww">
        {schedule.map((item, index) => {
        const status = statusFor(index);
        const isOpen = openId === item.id;
        const isCurrent = status === 'current';
        const detailRows = [{
          label: 'Duration',
          value: item.duration
        }, {
          label: 'Who',
          value: item.who
        }, {
          label: 'Where',
          value: item.where
        }].filter(row => row.value);
        return <li key={item.id} className={`border-b border-line ${isCurrent ? 'bg-surface' : ''}`} data-id="element-133" data-mp="7nx8lwmhnog3" data-mpi={index}>
              <button type="button" onClick={() => setOpenId(isOpen ? null : item.id)} aria-expanded={isOpen} className="flex w-full items-center gap-4 px-5 py-5 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-fg" data-id="element-134" data-mp="cf5isuvn7dub">
                <Marker status={status} data-id="element-135" data-mp="26tswtoof5nsc" />
                <span className="min-w-0 flex-1" data-id="element-136" data-mp="26wx6v2okfdqx">
                  <span className={`block font-display font-semibold tracking-display ${isCurrent ? 'text-[22px] leading-tight text-fg' : status === 'complete' ? 'text-[18px] leading-tight text-muted' : 'text-[18px] leading-tight text-fg'}`} data-id="element-137" data-mp="21x2jva7wv8b5">
                    {item.title}
                  </span>
                  {isCurrent ? <span className="mt-1.5 block text-[10px] font-medium uppercase tracking-label text-fg" data-id="element-138" data-mp="2ykpb8tq4o0hq">
                      Now
                    </span> : null}
                </span>
                <span className={`shrink-0 text-[13px] tabular-nums ${isCurrent ? 'text-fg' : 'text-dim'}`} data-id="element-139" data-mp="1rbvkddg9bw8c">
                  {item.start}
                </span>
              </button>

              <AnimatePresence initial={false} data-id="element-140" data-mp="218gprzw6v9cp">
                {isOpen ? <motion.div key="detail" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.24,
              ease: EASE
            }} className="overflow-hidden" data-id="element-141" data-mp="258iu6gtb6gvo">
                    <div className="px-5 pb-6 pl-[52px]" data-id="element-142" data-mp="1mag03nlwltr0">
                      {item.note ? <p className="text-[15px] leading-relaxed text-fg" data-id="element-143" data-mp="1mcsqmp5q9cfo">{item.note}</p> : null}
                      {detailRows.length > 0 && <dl className="mt-4 border-t border-line" data-id="element-144" data-mp="ttlpxzojzl3q">
                          {detailRows.map(row => <div key={row.label} className="flex items-baseline justify-between gap-6 border-b border-line py-2.5" data-id="element-145" data-mp="macgy7jylof7">
                              <MetaLabel tone="dim" data-id="element-146" data-mp="dcals8mwppzu">{row.label}</MetaLabel>
                              <dd className="text-right text-[13px] text-muted" data-id="element-147" data-mp="8abj70yzqx3y">{row.value}</dd>
                            </div>)}
                        </dl>}
                      {canEditSchedule && !isCurrent ? <button type="button" onClick={() => setCurrentTo(index)} className="mt-4 flex h-9 items-center gap-2 border border-line px-3 text-[10px] font-medium uppercase tracking-label text-muted transition-colors duration-150 ease-out hover:border-lineHi hover:text-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-fg" data-id="element-148" data-mp="3pr343z7oai44">
                          <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-149" data-mp="13h0z4yuz25k2" />
                          Mark As Current
                        </button> : null}
                    </div>
                  </motion.div> : null}
              </AnimatePresence>
            </li>;
      })}
      </ol>
    </div>;
}